import { useEffect, useState } from "react";
import PageHead from "@/components/PageHead";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import AOS from "aos";
import "aos/dist/aos.css";
import { useRouter } from "next/router";
import { useI18n } from "@/i18n/I18nProvider";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { getPaymentChannelsText } from "../../../locales/payment-channels";

export default function Index() {
  const router = useRouter();
  const { t, locale } = useI18n();
  const paymentChannelsText = getPaymentChannelsText(locale);

  const [openMap, setOpenMap] = useState({});
  const toggle = (i) => setOpenMap((p) => ({ ...p, [i]: !p[i] }));

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
    });
  }, []);
  return (
    <>
      <PageHead
        title={t.pages_main_tags.paymentChannels.title}
        description={t.pages_main_tags.paymentChannels.description}
        scripts={t.pages_main_tags.paymentChannels.scripts}
      />
      {/* Hero Section */}
      <Box
        sx={{
          backgroundImage:
            'url("https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/payment-channels-hero.svg")',
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          width: "100%",
          maxHeight: "610px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          p: "50px 26px",
          gap: "8px",
        }}
      >
        <Box
          component="h1"
          sx={{
            fontSize: { xs: "30px", sm: "44px" },
            fontWeight: "700",
            color: "#EAEAF3",
            textAlign: "center",
          }}
        >
          {paymentChannelsText.hero_section.title}
        </Box>
        <Box
          component="img"
          src="https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/line.svg"
          sx={{
            maxWidth: { xs: "280px", sm: "472px" },
          }}
        />
        <Box
          sx={{
            fontSize: { xs: "20px", sm: "28px" },
            fontWeight: "700",
            color: "#EAEAF3",
            maxWidth: {
              xs: "492px",
              md: "100%",
            },
            mb: 2,
            textAlign: "center",
          }}
        >
          {paymentChannelsText.hero_section.subtitle}
        </Box>
        <Box
          sx={{
            fontSize: "16px",
            fontWeight: "300",
            color: "#EAEAF3",
            maxWidth: "700px",
            textAlign: "center",
          }}
        >
          {paymentChannelsText.hero_section.description}
        </Box>
        <Box
          component="img"
          src={
            locale === "en"
              ? "https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/contact-logo-white-en.svg"
              : "https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/contact-logo-white-ar.svg"
          }
          sx={{
            paddingTop: "60px",
            width: { xs: "220px", md: "280px" },
            height: { xs: "100px", md: "120px" },
          }}
        />
      </Box>
      {/* Channels Section */}
      <Box
        data-aos="fade-up"
        sx={{
          backgroundColor: "#F8F8F8",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: { xs: "center", md: "flex-start" },
          gap: 2,
          px: { xs: "30px", md: "50px", lg: "120px" },
          py: { xs: "40px", md: "80px" },
        }}
      >
        <Box
          component="h2"
          sx={{
            fontSize: { xs: "24px", md: "38px" },
            fontWeight: "700",
            color: "#2D317F",
            textAlign: { xs: "center", md: "inherit" },
            maxWidth: { xs: "280px", md: "656px" },
          }}
        >
          {paymentChannelsText.channels_section.title}
        </Box>
        <Box
          component="img"
          src="https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/line.svg"
          sx={{
            width: { xs: "280px", md: "656px" },
            display: "block",
            mx: { xs: "auto", md: 0 },
            // maxHeight:"5px"
          }}
        />
        <Box
          component="p"
          sx={{
            fontSize: "16px",
            // fontWeight: "300",
            color: "#404456",
            maxWidth: "613px",
            // whiteSpace: "normal",
            "& p": {
              margin: 0,
              lineHeight: "24px",
            },
          }}
          dangerouslySetInnerHTML={{
            __html: paymentChannelsText.channels_section.description,
          }}
        />

        <Box
          sx={{
            fontSize: { xs: "18px", md: "20px" },
            fontWeight: "500",
            color: "#404456",
            // whiteSpace: "normal",
          }}
        >
          {paymentChannelsText.channels_section.channel_subtitle}
        </Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "flex-start",
            gap: 2,
          }}
        >
          {paymentChannelsText.channels_section.channels.map((item, index) => (
            <Box
              key={index}
              sx={{
                flex: {
                  xs: "0 0 45%", // 2 per row on mobile
                  sm: "0 0 30%", // 3 per row
                  md: "0 0 22%", // 4 per row
                  lg: "0 0 18%", // 5 per row
                },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
              }}
            >
              <Box
                component="img"
                src={item.logo}
                alt={item.title}
                sx={{
                  width: 96,
                  height: 96,
                  objectFit: "contain",
                  mb: 1,
                }}
              />
              <Box
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  wordWrap: "break-word",
                }}
              >
                {item.title}
              </Box>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            fontSize: "20px",
            fontWeight: "700",
            color: "#F18222",
            maxWidth: "421px",
          }}
        >
          {paymentChannelsText.channels_section.cta}
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          justifyContent: "space-between",
          alignItems: "center",
          gap: "40px",
          px: { xs: "30px", md: "50px", lg: "120px" },
          py: { xs: "40px", md: "80px" },
        }}
      >
        <Box
          data-aos="fade-up"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "24px",
          }}
        >
          <Box
            component="h2"
            sx={{
              fontSize: { xs: "24px", md: "32px" },
              fontWeight: "700",
              color: "#2D317F",
              width: { md: "709px" },
              whiteSpace: { xs: "normal", sm: "nowrap" },
              textAlign: { xs: "center", md: "inherit" },
            }}
          >
            {paymentChannelsText.img_paragraph_section.title}
          </Box>
          <Box
            component="img"
            src="https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/line.svg"
            sx={{
              maxWidth: { xs: "300px", sm: "500px", md: "709px" },
              display: "block",
              mx: { xs: "auto", md: 0 },
              // maxHeight:"5px"
            }}
          />
          <Box
            sx={{
              fontSize: { xs: "18px", md: "20px" },
              fontWeight: "500",
              color: "#404456",
              textAlign: { xs: "center", md: "inherit" },
              whiteSpace: "normal",
            }}
          >
            {paymentChannelsText.img_paragraph_section.subtitle}
          </Box>
          <Box
            component="p"
            sx={{
              fontSize: "16px",
              fontWeight: "300",
              color: "#404456",
              maxWidth: "613px",
              textAlign: { xs: "center", md: "inherit" },
              whiteSpace: "normal",
              "& p": {
                margin: 0,
                lineHeight: "24px",
              },
            }}
            dangerouslySetInnerHTML={{
              __html: paymentChannelsText.img_paragraph_section.description,
            }}
          />

          <Box
            sx={{
              fontSize: "20px",
              fontWeight: "700",
              color: "#F18222",
              maxWidth: "504px",
              textAlign: { xs: "center", md: "inherit" },
              mx: { xs: "auto", md: 0 },
            }}
          >
            {paymentChannelsText.img_paragraph_section.cta1}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              flexDirectionP: "row",
              flexWrap: "wrap",
              gap: "20px",
            }}
          >
            <a
              href="https://play.google.com/store/apps/details?id=eg.contact"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Box
                component="img"
                src="https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/google_store.svg"
                sx={{
                  width: "148.5px",
                  height: "auto",
                }}
              />
            </a>

            <a
              href="https://apps.apple.com/us/app/contactnow-%D9%83%D9%88%D9%86%D8%AA%D9%83%D8%AA/id1544159088"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Box
                component="img"
                src="https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/app_store.svg"
                sx={{
                  width: "148px",
                  height: "50px",
                }}
              />
            </a>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
              gap: "16px",
            }}
          >
            <Box
              sx={{
                fontSize: "20px",
                fontWeight: "700",
                color: "#F18222",
                maxWidth: "421px",
                textAlign: { xs: "center", md: "inherit" },
                mx: { xs: "auto", md: 0 },
              }}
            >
              {paymentChannelsText.img_paragraph_section.cta2}
            </Box>
            <Box
              onClick={() => router.push("/our-branches")}
              sx={{
                fontSize: "16px",
                fontWeight: "700",
                borderRadius: "8px",
                height: "40px",
                width: locale === "en" ? "150px" : "90px",
                backgroundColor: "#2D317F",
                color: "#F8F8F8",
                maxWidth: "421px",
                textAlign: "center",
                mx: { xs: "auto", md: 0 },
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              {paymentChannelsText.img_paragraph_section.cta_btn}

              {locale === "en" ? (
                <ArrowForwardIosIcon sx={{ fontSize: "14px", ml: "4px" }} />
              ) : (
                <ArrowBackIosIcon sx={{ fontSize: "14px", mr: "4px" }} />
              )}
            </Box>
          </Box>
        </Box>
        <Box
          data-aos="fade-up"
          component="img"
          src={`https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/homepage_img_${locale}.svg`}
          sx={{
            width: { xs: "300px", sm: "400px", md: "518px" },
          }}
        />
      </Box>
      {/* Contact Now Section */}
      <Box
        data-aos="fade-up"
        sx={{
          // width: "100%",
          // height: {xs:"",md:"",lg:"655px"},
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          // flexWrap: "wrap",
          justifyContent: "center",
          // justifyContent: "space-between",
          alignItems: "center",
          gap: 20,
          // gap: 2,
          px: { xs: "30px", md: "50px", lg: "120px" },
          py: { xs: "40px", md: "80px" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: { xs: "center", lg: "flex-start" },

            gap: 2,
          }}
        >
          <Box
            component="h2"
            sx={{
              fontSize: { xs: "24px", md: "38px" },
              fontWeight: "700",
              color: "#2D317F",
              // width: { xs: "400px",sm:"500px", md: "709px" },
              // whiteSpace: { xs: "", sm: "nowrap" },
            }}
          >
            {paymentChannelsText.contact_now_section.title1}
          </Box>
          <Box
            component="img"
            src="https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/line.svg"
            sx={{
              maxWidth: { xs: "250px", md: "450px" },
              display: "block",
              mx: { xs: "auto", md: 0 },
              // maxHeight:"5px"
            }}
          />
          <Box
            component="h2"
            sx={{
              fontSize: { xs: "24px", md: "38px" },
              fontWeight: "700",
              color: "#2D317F",
              // width: { xs: "400px",sm:"500px", md: "709px" },
              // whiteSpace: { xs: "", sm: "nowrap" },
            }}
          >
            {paymentChannelsText.contact_now_section.title2}
          </Box>
          <Box
            sx={{
              fontSize: { xs: "18px", md: "20px" },
              fontWeight: "600",
              color: "#404456",
              textAlign: { xs: "center", md: "inherit" },
              width: { xs: "300px", md: "613px" },
              whiteSpace: "normal",
            }}
          >
            {paymentChannelsText.contact_now_section.subtitle}
          </Box>
          <Box
            component="p"
            sx={{
              fontSize: "16px",
              // fontWeight: "300",
              color: "#404456",
              maxWidth: "613px",
              // whiteSpace: "normal",
              "& p": {
                margin: 0,
                lineHeight: "24px",
              },
            }}
            dangerouslySetInnerHTML={{
              __html: paymentChannelsText.contact_now_section.description,
            }}
          />
        </Box>
        <Box
          sx={{
            position: "relative",
            width: { xs: "250px", md: "388px" },
            // ml: { xs: 0, lg: "30px" },
          }}
        >
          {/* Bottom image (iPhone screen) */}
          <Box
            component="img"
            src={
              locale === "en"
                ? "https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/iphone_screen_en.png"
                : "https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/iphone_screen.png"
            }
            sx={{
              width: "100%",
              height: "auto",
              display: "block",
            }}
          />

          {/* Top image (screen content) */}
          <Box
            component="img"
            src={
              locale === "en"
                ? "https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/screen_contact_now_en.svg"
                : "https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/screen_contact_now.svg"
            }
            sx={{
              position: "absolute",
              top: "48%",
              left: locale === "en" ? "50%" : "45%",
              width: { xs: "300px", md: "120%" },
              height: "auto",
              transform: "translate(-50%, -50%)",
              zIndex: 1,
              filter:
                locale === "en"
                  ? "drop-shadow(0px 4px 15px rgba(0,0,0,0.3))"
                  : "none",
            }}
          />
        </Box>
      </Box>
      {/* Branches Section */}
      <Box
        data-aos="fade-up"
        sx={{
          // width: "100%",
          // height: {xs:"",md:"",lg:"655px"},
          backgroundColor: "#F8F8F8",
          display: "flex",
          flexDirection: { xs: "column-reverse", lg: "row" },
          // flexWrap: "wrap",
          justifyContent: "center",
          alignItems: "center",
          gap: "40px",
          px: { xs: "30px", md: "50px", lg: "120px" },
          py: { xs: "40px", md: "80px" },
        }}
      >
        <Box
          component="img"
          src="https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/branches.svg"
          sx={{
            width: { xs: "300px", lg: "400px" },
          }}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: { xs: "center", lg: "flex-start" },
            gap: 2,
          }}
        >
          <Box
            component="h2"
            sx={{
              fontSize: { xs: "24px", md: "38px" },
              fontWeight: "700",
              color: "#2D317F",
            }}
          >
            {paymentChannelsText.branches.title}
          </Box>
          <Box
            component="img"
            src="https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/line.svg"
            sx={{
              maxWidth: { xs: "250px", md: "450px" },
              display: "block",
              mx: { xs: "auto", md: 0 },
              // maxHeight:"5px"
            }}
          />
          <Box
            component="p"
            sx={{
              fontSize: "16px",
              // fontWeight: "300",
              color: "#404456",
              maxWidth: "613px",
              // whiteSpace: "normal",
              "& p": {
                margin: 0,
                lineHeight: "24px",
              },
            }}
            dangerouslySetInnerHTML={{
              __html: paymentChannelsText.branches.description,
            }}
          />
        </Box>
      </Box>
      {/* FAQs Section */}
      <Box
        data-aos="fade-up"
        sx={{
          // width: "100%",
          // height: {xs:"",md:"",lg:"655px"},
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          // flexWrap: "wrap",
          justifyContent: "center",
          alignItems: { xs: "center", lg: "flex-start" },
          gap: "40px",
          px: { xs: "30px", md: "50px", lg: "120px" },
          py: { xs: "40px", md: "80px" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Box
            component="h2"
            sx={{
              fontSize: { xs: "24px", md: "38px" },
              fontWeight: "700",
              color: "#2D317F",
            }}
          >
            {paymentChannelsText.faq.title}
          </Box>
          <Box
            component="img"
            src="https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/line.svg"
            sx={{
              maxWidth: { xs: "200px", md: "275px" },
              height: "7px",
              display: "block",
              mx: { xs: "auto", md: 0 },
              // maxHeight:"5px"
            }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            justifyContent: "center",
            gap: 2,
          }}
        >
          {paymentChannelsText.faq.faqs.map((item, index) => (
            <Accordion
              key={item.question}
              // disableGutters
              elevation={0}
              sx={{
                borderBottom: "1px solid rgba(0,0,0,0.2)",
                "&:before": { display: "none" },
                bgcolor: "#fff",
              }}
            >
              <AccordionSummary
                expandIcon={
                  locale == "en" ? (
                    <ChevronRightIcon sx={{ color: "#f27a21", fontSize: 30 }} />
                  ) : (
                    <ChevronLeftIcon sx={{ color: "#f27a21", fontSize: 30 }} />
                  )
                }
                sx={{
                  px: { xs: 2, md: 3 },
                  py: { xs: 1.5, md: 2 },
                  gap: 1,
                  "& .MuiAccordionSummary-content": {
                    ml: 1,
                    my: 0,
                  },
                  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                    transform: "rotate(90deg)",
                  },
                }}
              >
                <Typography
                  component="h3"
                  sx={{ fontWeight: 700, fontSize: { xs: 16, md: 18 } }}
                >
                  {item.question}
                </Typography>
              </AccordionSummary>

              <AccordionDetails sx={{ px: { xs: 2, md: 3 }, pt: 0, pb: 2 }}>
                <Typography
                  sx={{ lineHeight: 1.7, fontSize: { md: 15 } }}
                  dangerouslySetInnerHTML={{ __html: item.answer }}
                ></Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Box>
      {/* CTA Section */}
      <Box
        data-aos="fade-up"
        sx={{
          backgroundColor: "#F8F8F8",
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          // flexWrap: "wrap",
          justifyContent: "center",
          alignItems: { xs: "center", lg: "flex-start" },
          px: { xs: "30px", md: "50px", lg: "120px" },
          pt: { xs: "100px", md: "205px" },
          pb: { xs: "40px", md: "80px" },
        }}
      >
        <Box
          sx={{
            background:
              locale === "en"
                ? {
                    xs: `linear-gradient( to top, #2D317F1A 70%, #2D317F 30%)`,
                    md: `linear-gradient( to top, #2D317F1A 55%, #2D317F 45%)`,
                    lg: `linear-gradient( to left, #2D317F1A 60%, #2D317F 40%)`,
                  }
                : {
                    xs: `linear-gradient( to top, #2D317F1A 70%, #2D317F 30%)`,
                    md: `linear-gradient( to top, #2D317F1A 55%, #2D317F 45%)`,
                    lg: `linear-gradient( to right, #2D317F1A 55%, #2D317F 45%)`,
                  },
            width: { xs: "300px", md: "450px", lg: "1198px" },
            height: { xs: "700px", md: "800px", lg: "328px" },
            borderRadius: "24px",
            display: "flex",
            justifyContent: { xs: "center", lg: "flex-start" },
            alignItems: { xs: "center", lg: "flex-end" },
            pr: { xs: "0", lg: "40px" },
            // pl: {xs:"0",md:"20px",lg:"0px"},
            pb:
              locale === "en"
                ? { xs: "78px", md: "94px", lg: "0" }
                : { xs: "54px", md: "89px", lg: "0" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "center", lg: "flex-start" },
              alignItems: { xs: "center", lg: "flex-end" },
              flexDirection: { xs: "column", lg: "row" },
              gap: { xs: "30px", md: "30px", lg: "80px" },
            }}
          >
            <Box
              component="img"
              src={
                locale === "en"
                  ? "https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/half_phone_screen_en.png"
                  : "https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/half_phone_screen.png"
              }
              sx={{
                width:
                  locale === "en"
                    ? { xs: "220px", md: "350px", lg: "381px" }
                    : { xs: "250px", md: "400px", lg: "438.5px" },
                height: "auto",
                ml:
                  locale === "en"
                    ? { xs: "0px", md: "0px", lg: "50px" }
                    : { xs: "30px", md: "35px", lg: "0px" },
              }}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: { xs: "center", lg: "flex-start" },
                flexDirection: "column",
                gap: "20px",
                pb: "80px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: { xs: "column", lg: "row" },
                  gap: "20px",
                }}
              >
                <Box
                  component="img"
                  src="https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/qr_code.png"
                  sx={{
                    width: "140px",
                    height: "auto",
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: { xs: "center", lg: "flex-start" },
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      fontSize: { xs: "18px", md: "24px" },
                      fontWeight: "600",
                      color: "#404456",
                      maxWidth: "257px",
                      textAlign: { xs: "center", lg: "inherit" },
                      whiteSpace: "normal",
                    }}
                  >
                    {paymentChannelsText.cta_section.title}
                  </Box>
                  <Box
                    sx={{
                      fontSize: { xs: "16px", md: "16px" },
                      fontWeight: "600",
                      color: "#404456",
                      textAlign: { xs: "center", lg: "inherit" },
                      whiteSpace: "normal",
                    }}
                  >
                    {paymentChannelsText.cta_section.subtitle}
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirectionP: "row",
                  flexWrap: "wrap",
                  gap: "20px",
                }}
              >
                <a
                  href="https://play.google.com/store/apps/details?id=eg.contact"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Box
                    component="img"
                    src="https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/google_store.svg"
                    sx={{
                      width: "148px",
                      height: "50px",
                    }}
                  />
                </a>

                <a
                  href="https://apps.apple.com/us/app/contactnow-%D9%83%D9%88%D9%86%D8%AA%D9%83%D8%AA/id1544159088"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Box
                    component="img"
                    src="https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/app_store.svg"
                    sx={{
                      width: "148px",
                      height: "50px",
                    }}
                  />
                </a>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}
